import {
	buildBreadcrumbManager,
	buildFieldSortCriterion,
	buildQuerySummary,
	buildRelevanceSortCriterion,
	buildResultList,
	buildSearchParameterManager,
	buildSort,
	loadAdvancedSearchQueryActions,
	loadQueryActions,
	loadSearchAnalyticsActions,
	SearchEngine,
	SearchParameters,
	SortOrder
} from "@coveo/headless";
import { useEffect, useMemo, useState } from "react";
import deserializeUrl from "components/coveo-headless/utils/deserialize-url";
import serializeUrl from "components/coveo-headless/utils/serialize-url";
import {
	CoveoHeadlessData,
	CoveoHeadlessTemplate,
	SortByDictionary,
	standaloneSearchBoxStorageKey,
	CoveoHeadlessSortBy
} from "components/coveo-headless/types/coveo-headless-types";
import { generateFacets } from "components/coveo-headless/controllers/Controllers";
import TotalResults from "components/coveo-headless/components/TotalResults";
import setDefaultSort from "components/coveo-headless/utils/setDefaultSort";
import {
	registerField,
	registerSearchHub,
	launchAnalytics
} from "components/coveo-headless/utils/initAnalytics";
import { setBodyPositionFixed } from "utils/dom";
import SearchCallToActionBar from "./SearchCallToActionBar";
import SearchPageMobileFilters from "./SearchPageMobileFilters";
import SearchPageHeaderSort from "./SearchPageHeaderSort";
import SortMobile from "components/coveo-headless/components/SortMobile";
import FacetGroupMobile from "components/coveo-headless/components/FacetGroupMobile";
import SearchPageContent from "./SearchPageContent";
import { SearchPageResultsPlaceholderPage } from "../search-results-placeholders/SearchPageResultsPlaceholderPage";

type ClientSiteSearchPageProps = {
	engine: SearchEngine;
	jsonData: CoveoHeadlessData;
	pageType: string;
	searchHub: string;
	templates: CoveoHeadlessTemplate[];
	uniqueId: string;
};

const ClientSiteSearchPage = ({
	engine,
	jsonData,
	pageType,
	searchHub,
	templates,
	uniqueId
}: ClientSiteSearchPageProps) => {
	const {
		aq,
		context,
		fields,
		filters,
		showMoreText,
		tabs,
		resultDescription,
		filterCloseText,
		facetQuantifier
	} = jsonData;
	const { facetName } = filters;

	const mobileFacetGroupLabel = "filter";
	// available sort values
	const criteriaDictionary: SortByDictionary[] = [
		{
			name: "Relevancy",
			criterion: buildRelevanceSortCriterion()
		},
		{
			name: "Newest",
			criterion: buildFieldSortCriterion("publishdate", SortOrder.Descending)
		},
		{
			name: "Oldest",
			criterion: buildFieldSortCriterion("publishdate", SortOrder.Ascending)
		},
		{
			name: "Alphabetical",
			criterion: buildFieldSortCriterion("resulttitle", SortOrder.Ascending)
		}
	];
	const defaultSort = setDefaultSort(filters.sortCriteria, criteriaDictionary);
	// string values to use for serialize-url function
	const sortByDictionary = [
		CoveoHeadlessSortBy.Relevancy,
		CoveoHeadlessSortBy.PublishDateAsc,
		CoveoHeadlessSortBy.PublishDateDesc,
		CoveoHeadlessSortBy.Alphabetical
	];

	const sortCriteria = useMemo(() => buildSort(engine), [engine]);
	const facetsMemo = useMemo(
		() => generateFacets(engine, facetName, "score", 100),
		[engine, facetName]
	);
	const querySummary = useMemo(() => buildQuerySummary(engine), [engine]);
	const resultList = useMemo(() => buildResultList(engine), [engine]);
	const breadcrumbs = useMemo(() => buildBreadcrumbManager(engine), [engine]);
	const [toggleMobileSort, setToggleMobileSort] = useState(false);
	const [mobileSortBy, setMobileSortBy] = useState("");
	const [isMobile, setIsMobile] = useState(false);
	const [selectedDropdown, setSelectedDropdown] = useState(-1);
	const [toggleMobileFacetGroup, setToggleMobileFacetGroup] = useState(false);
	const [toggleMobileFacetList, setToggleMobileFacetList] = useState(false);
	const [mobileFilterCount, setMobileFilterCount] = useState(0);
	const [mobileSelectedFacets, setMobileSelectedFacets] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [loaderState, setLoaderState] = useState({ isLoading: true });

	useEffect(() => {
		resultList.subscribe(() => {
			setLoaderState(resultList.state);
		});
	}, [resultList]);

	useEffect(() => {
		if (loaderState?.isLoading === false) {
			setIsLoading(false);
		}
	}, [loaderState]);

	useEffect(() => {
		const isMobileFunc = () => {
			if (window.innerWidth < 1024) {
				return setIsMobile(true);
			}
			setIsMobile(false);
		};
		isMobileFunc();
		window.addEventListener("resize", isMobileFunc);

		return () => {
			window.removeEventListener("resize", isMobileFunc);
		};
	}, []);

	useEffect(() => {
		const hash = window.location.hash.slice(1);
		let parameters: SearchParameters = deserializeUrl(
			aq,
			defaultSort.criteria,
			sortByDictionary,
			tabs,
			hash
		);
		if (Object.keys(parameters).length === 0) {
			parameters.aq = aq;
			parameters.sortCriteria = defaultSort.criteria;
		}

		// on page load, set sort by value for mobile
		setMobileSortBy(defaultSort.sortName);
		const searchParameterManager = buildSearchParameterManager(engine, {
			initialState: {
				parameters: {
					aq: parameters.aq,
					q: parameters.q,
					f: parameters.f,
					sortCriteria: parameters.sortCriteria
				}
			}
		});
		// when user filters via UI, update url hash
		searchParameterManager.subscribe(() => {
			const url = serializeUrl(
				searchParameterManager.state.parameters,
				sortCriteria.state.sortCriteria,
				pageType
			);
			if (searchParameterManager.state.parameters.f) {
				const newMobileFilterCount = Object.keys(searchParameterManager.state.parameters.f).reduce(
					(acc, val) => {
						acc += searchParameterManager.state.parameters.f![val].length;
						return acc;
					},
					0
				);
				setMobileFilterCount(newMobileFilterCount);
				setMobileSelectedFacets(searchParameterManager.state.parameters.f);
			} else {
				setMobileFilterCount(0);
				setMobileSelectedFacets({});
			}

			window.history.pushState(null, document.title, url);
		});

		/**
		 * when user types into url bar, deserialize new parameters, update UI
		 * update searchParameterManager with new query parameters
		 */
		const onHashChange = () => {
			const hash = window.location.hash.slice(1);
			const parameters = deserializeUrl(aq, defaultSort.criteria, sortByDictionary, tabs, hash);
			searchParameterManager.synchronize(parameters);
		};

		const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);
		const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);

		const { updateQuery } = loadQueryActions(engine);
		const { logSearchFromLink, logOmniboxFromLink } = loadSearchAnalyticsActions(engine);

		engine.dispatch(updateAdvancedSearchQueries({ aq: aq }));

		// Example redirectSearchData from typed value:
		// {"value":"etfs","analytics":{"cause":"searchFromLink","metadata":null}}
		//  Example redirectSearchData from selected coveo suggestion:
		// {"value":"tuning out the noise","analytics":{"cause":"omniboxFromLink",
		// "metadata":{"suggestionRanking":1,"partialQuery":"out","partialQueries":["out"],
		// "suggestions":["out","tuning out the noise"],
		// "querySuggestResponseId":"e24a17d2-70cb-426f-af68-3f3432f3162b"}}}
		// We allow people to hit enter without any input value which results in:
		// {"value":"","analytics":{"cause":"","metadata":null}}
		const redirectSearchData = localStorage.getItem(standaloneSearchBoxStorageKey);

		if (redirectSearchData) {
			localStorage.removeItem(standaloneSearchBoxStorageKey);

			const { value, analytics } = JSON.parse(redirectSearchData);
			const { cause, metadata } = analytics;

			if (value) {
				const event =
					cause === "searchFromLink" ? logSearchFromLink() : logOmniboxFromLink(metadata);
				engine.dispatch(updateQuery({ q: value }));
				engine.executeFirstSearch(event);
			} else {
				engine.executeFirstSearch(logInterfaceLoad());
			}
		} else {
			engine.executeFirstSearch(logInterfaceLoad());
		}

		window.addEventListener("hashchange", onHashChange);

		return () => {
			window.removeEventListener("hashchange", onHashChange);
		};
		// adding all dependencies react recommends cause coveo to go into an infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [aq, engine, filters.pipeline, tabs]);

	launchAnalytics(engine, context);
	registerField(engine, fields);
	registerSearchHub(engine, searchHub);

	return isLoading ? (
		<SearchPageResultsPlaceholderPage numberOfCards={Number(jsonData.filters.numberOfResults)} />
	) : (
		<>
			<div
				className={`body-mask-target ${
					toggleMobileSort || toggleMobileFacetGroup ? "body-mask" : ""
				} `}
				role="button"
				tabIndex={0}
				onClick={() => {
					setBodyPositionFixed();
					setToggleMobileSort(false);
					setToggleMobileFacetGroup(false);
					setSelectedDropdown(-1);
					setToggleMobileFacetList(false);
				}}
				onKeyDown={() => {
					setBodyPositionFixed();
					setToggleMobileSort(false);
					setToggleMobileFacetGroup(false);
					setSelectedDropdown(-1);
					setToggleMobileFacetList(false);
				}}
			></div>
			<section className="search-page-header-mobile">
				<div className="search-page-header-content">
					<TotalResults
						controller={querySummary}
						showInFilters={false}
						pageType={pageType}
						totalResultsDescription={resultDescription}
					/>
				</div>
			</section>
			{isMobile && <SearchCallToActionBar />}
			<SearchPageMobileFilters
				isMobile={isMobile}
				mobileFilterCount={mobileFilterCount}
				mobileSortBy={mobileSortBy}
				querySummary={querySummary}
				toggleMobileFacetGroup={toggleMobileFacetGroup}
				toggleMobileSort={toggleMobileSort}
				setToggleMobileSort={setToggleMobileSort}
				setToggleMobileFacetGroup={setToggleMobileFacetGroup}
			/>
			<section className="search-page-header-desktop">
				<div className="search-page-header-content">
					<TotalResults
						controller={querySummary}
						showInFilters={false}
						pageType={pageType}
						totalResultsDescription={resultDescription}
					/>
					<SearchPageHeaderSort
						controller={querySummary}
						criteriaDictionary={criteriaDictionary}
						sortCriteria={sortCriteria}
					/>
				</div>
			</section>
			{!isMobile && <SearchCallToActionBar />}
			{toggleMobileSort && (
				<div
					className={`coveo-headless-mobile-tab-section ${
						toggleMobileSort ? "coveo-headless-mobile-tab-section-show" : ""
					}`}
				>
					<SortMobile
						controller={sortCriteria}
						criteria={criteriaDictionary}
						setMobileSortBy={setMobileSortBy}
						setToggleMobileSort={setToggleMobileSort}
					/>
				</div>
			)}
			{toggleMobileFacetGroup && (
				<div
					className={`coveo-headless-mobile-facet-group ${
						toggleMobileFacetGroup ? "coveo-headless-mobile-facet-group-show" : ""
					} ${toggleMobileFacetList ? "coveo-headless-mobile-facet-group-list-open" : ""}`}
				>
					<FacetGroupMobile
						breadcrumbs={breadcrumbs}
						facets={facetsMemo}
						fields={facetName}
						mobileFacetGroupLabel={mobileFacetGroupLabel}
						mobileFilterCount={mobileFilterCount}
						mobileSelectedFacets={mobileSelectedFacets}
						toggleMobileFacetList={toggleMobileFacetList}
						pageType={pageType}
						selectedDropdown={selectedDropdown}
						setSelectedDropdown={setSelectedDropdown}
						setToggleMobileFacetGroup={setToggleMobileFacetGroup}
						setToggleMobileFacetList={setToggleMobileFacetList}
						facetQuantifier={facetQuantifier}
					>
						{filterCloseText}
					</FacetGroupMobile>
				</div>
			)}
			<div className="container">
				<SearchPageContent
					controller={querySummary}
					facets={facetsMemo}
					breadcrumbs={breadcrumbs}
					page={pageType}
					resultList={resultList}
					headlessEngine={engine}
					showMoreText={showMoreText}
					templates={templates}
					facetName={facetName}
					uniqueId={uniqueId}
				/>
			</div>
		</>
	);
};

export default ClientSiteSearchPage;
